import { useState } from "react";

import { requestTranscription } from "@/api/endpoints/episodes";
import {
  INSTAGRAM_TITLE,
  SOURCE,
  TRANSCRIPT_IN_PROGRESS_STATUSES,
  TRANSCRIPT_STATUS,
  type TranscriptStatus,
} from "@/constants";
import { useMentionCard } from "@/containers/MentionCard/context";
import { useMessages } from "@/containers/Messages/MessageContext";
import { useMentionCards } from "@/contexts/MentionCardsContext";
import { FEATURE } from "@/features";
import { useHasFeature } from "@/hooks/useHasFeature";

const TranscribeEpisode: React.FunctionComponent<{
  component: React.ElementType;
}> = ({ component: Component, ...rest }) => {
  const {
    channel: { channel_type },
    episode,
  } = useMentionCard();

  const is_text_source = [
    "facebook",
    "instagram",
    "tiktok",
    "twitter",
    "linkedin",
    "news",
  ].includes(channel_type);

  const [loading, setLoading] = useState(false);
  const { danger, success } = useMessages();
  const { patchMentionCard } = useMentionCards();

  const handleRequestTranscript = (e: MouseEvent) => {
    e.preventDefault();

    requestTranscription(episode.pk.toString())
      .then(() => {
        setLoading(true);
        patchMentionCard({
          id: episode.id,
          episode: {
            ...episode,
            transcript_status: TRANSCRIPT_STATUS.QUEUED_FOR_TRANSCRIPTION,
          },
        });
        success({
          title: "",
          message: L.CUSTOMERS_REQUEST_TRANSCRIBE_SUCCESS_MESSAGE,
        });
      })
      .catch(() => {
        setLoading(false);
        danger({
          title: L.CUSTOMERS_SEARCH_500_ERROR_TITLE,
          message: L.CUSTOMERS_SEARCH_500_ERROR_MESSAGE,
        });
      });
  };

  const transcriptionInProgress =
    !!episode.transcript_status &&
    (TRANSCRIPT_IN_PROGRESS_STATUSES as readonly TranscriptStatus[]).includes(
      episode.transcript_status
    );

  const isTiktokOrInstagramOrNonText =
    !is_text_source ||
    channel_type === SOURCE.TIKTOK ||
    (channel_type == SOURCE.INSTAGRAM &&
      (episode.title === INSTAGRAM_TITLE.VIDEO ||
        episode.title === INSTAGRAM_TITLE.STORY));

  const canTranscribe = useHasFeature(FEATURE.HAS_REQUEST_TRANSCRIPT_FEATURE);

  const hasTranscriptionFeatures = episode.pk && canTranscribe;

  const episodeCanBeTranscribed =
    !episode.has_transcript && isTiktokOrInstagramOrNonText;

  const showRequestTranscription =
    hasTranscriptionFeatures && episodeCanBeTranscribed;

  if (!showRequestTranscription) {
    return null;
  }
  return (
    <Component
      onClick={handleRequestTranscript}
      disabled={
        loading || (transcriptionInProgress && isTiktokOrInstagramOrNonText)
      }
      {...rest}
    >
      {transcriptionInProgress
        ? L.MENTIONS_TRANSCRIBE_IN_PROGRESS
        : L.MENTIONS_TRANSCRIBE_EPISODE}
    </Component>
  );
};

export default TranscribeEpisode;
